import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { PlusCircle, Loader2, Clock, CheckCircle, XCircle, AlertCircle, Upload, FileText, Trash2, Eye, Paperclip } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientFundRequestsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [requestDocuments, setRequestDocuments] = useState([]);
  const [viewDocsOpen, setViewDocsOpen] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  
  const [formData, setFormData] = useState({
    amount_requested: '',
    currency: 'ZAR',
    purpose: '',
    deal_type: 'SRA',
    expected_tenor_days: '',
    supplier_name: '',
    customer_name: '',
    product_description: '',
    collateral_description: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchRequests();
  }, [token]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/client/fund-requests`, { headers });
      setRequests(response.data || []);
    } catch (err) {
      console.error('Error fetching fund requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (requestId) => {
    try {
      setLoadingDocs(true);
      const response = await axios.get(`${API_URL}/api/client/fund-requests/${requestId}/documents`, { headers });
      setRequestDocuments(response.data || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setRequestDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleViewDocuments = async (request) => {
    setSelectedRequest(request);
    await fetchDocuments(request.id);
    setViewDocsOpen(true);
  };

  const handleUploadOpen = (request) => {
    setSelectedRequest(request);
    setSelectedFiles([]);
    setUploadOpen(true);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadDocuments = async () => {
    if (!selectedFiles.length || !selectedRequest) {
      toast.error('Please select files to upload');
      return;
    }

    try {
      setUploading(true);
      
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        
        await axios.post(
          `${API_URL}/api/client/fund-requests/${selectedRequest.id}/documents`,
          formData,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
        );
      }
      
      toast.success(`${selectedFiles.length} document(s) uploaded successfully`);
      setUploadOpen(false);
      setSelectedFiles([]);
      fetchRequests();
    } catch (err) {
      console.error('Error uploading documents:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount_requested || !formData.purpose || !formData.expected_tenor_days) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await axios.post(`${API_URL}/api/client/fund-requests`, {
        ...formData,
        amount_requested: parseFloat(formData.amount_requested),
        expected_tenor_days: parseInt(formData.expected_tenor_days)
      }, { headers });
      
      toast.success('Fund request submitted successfully');
      setNewRequestOpen(false);
      setFormData({
        amount_requested: '',
        currency: 'ZAR',
        purpose: '',
        deal_type: 'SRA',
        expected_tenor_days: '',
        supplier_name: '',
        customer_name: '',
        product_description: '',
        collateral_description: ''
      });
      fetchRequests();
      
      // Prompt to upload documents for the new request
      const newRequest = response.data;
      if (newRequest && newRequest.id) {
        setTimeout(() => {
          toast.info('You can now upload supporting documents for this request', {
            action: {
              label: 'Upload',
              onClick: () => handleUploadOpen(newRequest)
            }
          });
        }, 1000);
      }
    } catch (err) {
      console.error('Error submitting fund request:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit fund request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-5 w-5 text-amber-500" />;
      case 'under_review': return <AlertCircle className="h-5 w-5 text-blue-500" />;
      case 'approved': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'rejected': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'disbursed': return <CheckCircle className="h-5 w-5 text-[#0D3B66]" />;
      default: return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-amber-100 text-amber-800',
      under_review: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      disbursed: 'bg-[#0D3B66] text-white'
    };
    return variants[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6" data-testid="client-fund-requests-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">Fund Requests</h1>
          <p className="text-[#6C757D]">Submit and track your funding requests</p>
        </div>
        <Button
          className="bg-[#0D3B66] hover:bg-[#0A2F52]"
          onClick={() => setNewRequestOpen(true)}
          data-testid="new-request-btn"
        >
          <PlusCircle className="h-4 w-4 mr-2" />
          New Request
        </Button>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#0D3B66]" />
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-[#6C757D] mb-4">No fund requests yet</p>
            <Button onClick={() => setNewRequestOpen(true)}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Submit Your First Request
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <Card key={request.id} className="overflow-hidden">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {getStatusIcon(request.status)}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-lg text-[#212529]">
                          {formatCurrency(request.amount_requested)}
                        </p>
                        <Badge className={getStatusBadge(request.status)}>
                          {request.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <p className="text-[#6C757D] mb-2">{request.purpose}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-[#6C757D]">
                        <span>Deal Type: <strong>{request.deal_type}</strong></span>
                        <span>Tenor: <strong>{request.expected_tenor_days} days</strong></span>
                        <span>Submitted: {formatDate(request.created_at)}</span>
                      </div>
                      {request.admin_notes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-[#6C757D]">
                            <strong>Admin Notes:</strong> {request.admin_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Document Actions */}
                  <div className="flex flex-col gap-2 min-w-[160px]">
                    <div className="flex items-center gap-2 text-sm text-[#6C757D]">
                      <Paperclip className="h-4 w-4" />
                      <span>{request.document_count || 0} document(s)</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDocuments(request)}
                        data-testid={`view-docs-${request.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUploadOpen(request)}
                        data-testid={`upload-docs-${request.id}`}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Upload
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Request Dialog */}
      <Dialog open={newRequestOpen} onOpenChange={setNewRequestOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Fund Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="amount_requested">Amount Requested (ZAR) *</Label>
                <Input
                  id="amount_requested"
                  name="amount_requested"
                  type="number"
                  value={formData.amount_requested}
                  onChange={handleInputChange}
                  placeholder="e.g. 500000"
                  required
                  data-testid="amount-input"
                />
              </div>
              <div>
                <Label htmlFor="deal_type">Deal Type *</Label>
                <Select value={formData.deal_type} onValueChange={(v) => handleSelectChange('deal_type', v)}>
                  <SelectTrigger data-testid="deal-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SRA">SRA - Sale & Repurchase</SelectItem>
                    <SelectItem value="TLA">TLA - Term Loan</SelectItem>
                    <SelectItem value="BF">BF - Bridging Finance</SelectItem>
                    <SelectItem value="OF">OF - Order Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="expected_tenor_days">Expected Tenor (Days) *</Label>
                <Input
                  id="expected_tenor_days"
                  name="expected_tenor_days"
                  type="number"
                  value={formData.expected_tenor_days}
                  onChange={handleInputChange}
                  placeholder="e.g. 90"
                  required
                  data-testid="tenor-input"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="purpose">Purpose / Description *</Label>
                <Textarea
                  id="purpose"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleInputChange}
                  placeholder="Describe the purpose of the funding..."
                  required
                  rows={3}
                  data-testid="purpose-input"
                />
              </div>
              <div>
                <Label htmlFor="supplier_name">Supplier Name</Label>
                <Input
                  id="supplier_name"
                  name="supplier_name"
                  value={formData.supplier_name}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label htmlFor="customer_name">Customer Name</Label>
                <Input
                  id="customer_name"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="product_description">Product Description</Label>
                <Input
                  id="product_description"
                  name="product_description"
                  value={formData.product_description}
                  onChange={handleInputChange}
                  placeholder="What product/service is being financed?"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="collateral_description">Collateral / Security</Label>
                <Textarea
                  id="collateral_description"
                  name="collateral_description"
                  value={formData.collateral_description}
                  onChange={handleInputChange}
                  placeholder="Describe any collateral or security offered..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewRequestOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#0D3B66] hover:bg-[#0A2F52]" data-testid="submit-request-btn">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Documents Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Supporting Documents</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Files</Label>
              <Input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                onChange={handleFileSelect}
                className="mt-2"
                data-testid="file-upload-input"
              />
              <p className="text-xs text-[#6C757D] mt-1">
                Accepted: PDF, Images, Word, Excel (Max 10MB each)
              </p>
            </div>
            
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Files ({selectedFiles.length})</Label>
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#6C757D]" />
                      <div>
                        <p className="text-sm font-medium truncate max-w-[200px]">{file.name}</p>
                        <p className="text-xs text-[#6C757D]">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadDocuments}
              disabled={uploading || selectedFiles.length === 0}
              className="bg-[#0D3B66] hover:bg-[#0A2F52]"
              data-testid="submit-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload {selectedFiles.length} File(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Documents Dialog */}
      <Dialog open={viewDocsOpen} onOpenChange={setViewDocsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Documents</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {loadingDocs ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#0D3B66]" />
              </div>
            ) : requestDocuments.length === 0 ? (
              <div className="text-center py-8 text-[#6C757D]">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No documents uploaded yet</p>
                <Button
                  className="mt-4 bg-[#0D3B66] hover:bg-[#0A2F52]"
                  onClick={() => {
                    setViewDocsOpen(false);
                    handleUploadOpen(selectedRequest);
                  }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Documents
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {requestDocuments.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-[#0D3B66]" />
                      <div>
                        <p className="font-medium text-sm">{doc.filename}</p>
                        <p className="text-xs text-[#6C757D]">
                          Uploaded {new Date(doc.uploaded_at).toLocaleDateString('en-ZA')}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline">{doc.file_type}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDocsOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setViewDocsOpen(false);
                handleUploadOpen(selectedRequest);
              }}
              className="bg-[#0D3B66] hover:bg-[#0A2F52]"
            >
              <Upload className="h-4 w-4 mr-2" />
              Add More
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientFundRequestsPage;
